/*
 * Things to watch out for:
 * Images loaded from S3 have 202305 date hardcoded
 * If we get to a point where we are updating monthly,
 * we will need to figure out how to have that updated.
 * Maybe from Version.json?
 *
 */

import React, { Component, PropTypes } from 'react';
import CSSModules from 'react-css-modules'
import config from 'config'
import Helmet from 'react-helmet'
import Select from 'react-select'
import {SlideDown} from 'react-slidedown'
import {Row, Col} from 'react-flexbox-grid'
import Loader from 'react-loader'
import { Link as ScrollLink, scroller } from 'react-scroll'
import {
  ContainerDivs,
  Link,
  LinkIcon,
  SrOnly,
  SignupForm,
  ScrollToTop,
  SelectDropdownPlan
} from '../../components'
import Styles from './FossilFuelBondsIn401ks.css'
import format from '../../helpers/format'
import container from '../../helpers/container'

import imageSocialShare from '../../../static/images/fossil-fuel-bonds-social-share.png'

// used to control chart image loading from S3
const currYYYYMM = '202305'

// function aliases
// fm -> format.money
const fm = (m, decimals=2) => {
  return format.money(m, true, 'USD', true, 2)
}
// fp -> format.perc
const fp = (p, round=false, decimals=2) => { return format.perc(p, round, decimals) }


//
//
//
//
//
//
/**
 * <LedeNav> component
 * Display: layout, headline, text of lede at top of page,
 *          Nav with <ScrollLink> to scroll down to findings, results, etc.
 * @param title: Page/window title
 */
@CSSModules(Styles, {allowMultiple:true})
class LedeNav extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired
  }

  render() {
    const aysLogo = require( '../../../static/images/AsYouSow_Logo_color.svg' )
    //
    return (
      <div styleName='lede-wrap'>
        <ContainerDivs>
          <div styleName='lede-h1-wrap'>
            <h1>{this.props.title}</h1>
          </div>
          <div styleName='lede-nav-flex'>
            { /* LEDE NAV */ }
            <nav styleName='scroll-nav-btn-wrap'>
              {
                [['issue','The bond problem'],
                 ['key-findings','Key findings'],
                 ['retirement-plans','Retirement plans'],
                 ['target-dates','Target date series'],
                 ['recommendations','Recommendations'],
                 ['methodology','Methodology'],
                ].map((s, i) => {
                  return <ScrollLink
                    activeClass='active-scroll-link'
                    to={s[0]}
                    key={'scroll-link-'+s[0]}
                    spy
                    smooth
                    offset={-70}
                    duration={500}
                  >
                    <div styleName='scroll-nav-btn'>
                      {s[1]}
                    </div>
                  </ScrollLink>
                })
              }
            </nav>
            <div styleName='lede-p-wrap'>
              <p>
                Retirement plans don't just invest in stocks of high-carbon companies.
                They also hold corporate bonds issued by
                those same companies. Corporate bonds are one of the
                main ways fossil fuel companies raise money to
                expand their operations.
              </p>
              <p>
                <b>If there are fossil fuel bonds in
                your 401(k), you're effectively
                lending your retirement savings to companies
                so they can mine for coal and
                drill for oil.</b>
              </p>
            </div>
          </div>
          <div styleName='lede-ays-logo-wrap'>
            <Link
              to={config.links.ays}
              title='As You Sow'
            >
              <img
                src={aysLogo}
                alt='As You Sow'
                styleName='lede-ays-logo'
              />
              <SrOnly>As You Sow</SrOnly>
            </Link>
          </div>
        </ContainerDivs>
      </div>
    )
  }
}


//
// <TheIssue> component
// Display: 3 circle images w/ captions,
//          button link to Bond Climate Risks page
//
@CSSModules(Styles, {allowMultiple:true})
class TheIssue extends Component {
  render() {
    const icon1 = require( '../../../static/images/bonds-issue-circle-1.png' )
    const icon2 = require( '../../../static/images/bonds-issue-circle-2.png' )
    const icon3 = require( '../../../static/images/bonds-issue-circle-3.png' )
    return (
      <div styleName='issue-wrap' id='issue'>
        <ContainerDivs>
          <h2>
            The bond problem
          </h2>
          <div styleName='_123-flex'>
            {
              [
                [icon1, 'Fossil fuel companies issue bonds',
                 (<span>Fossil fuel companies issue bonds to raise money to
                        develop new coal, oil, and gas projects. Retirement
                        plans invest employee savings in those
                        bonds.</span>)],
                [icon2, 'There can be no new coal, oil, and gas development',
                 (<span>The International Energy Agency has warned that there
                        can be no new coal, oil, and gas development if
                        humanity wants to prevent dangerous
                        warming.</span>)],
                [icon3,
                 'Savers face the risk of climate-related financial losses',
                 (<span>Those who have invested in fossil fuel companies,
                        including people who have purchased the companies’
                        stocks or bonds, face the risk of climate-related
                        financial losses.</span>)]
              ].map((e, i11) => {
                const [icon, altText, text] = e
                return (
                  <div styleName='_123-cell' key={'issue-cell-'+i11}>
                    <div styleName='_123-image'>
                      <img src={icon} alt={altText} />
                    </div>
                    <h3 styleName='_123-copy'>{text}</h3>
                  </div>
                )
              })
            }
          </div>
          <div styleName='issue-risk-link-wrap'>
            <Link
              to={config.routes.fossilFuelBondRisks}
              title='Financial risks of fossil fuel bonds'
            >
              <div styleName='issue-risk-link'>
                Financial risks of fossil fuel bonds<LinkIcon/>
              </div>
            </Link>
          </div>
          <ScrollToTop color='red'/>
        </ContainerDivs>
      </div>
    )
  }
}


//
// <KeyFindings> component
// Display: text framing key findings
// Includes: <BulletAccordion> of key findings
//
@CSSModules(Styles, {allowMultiple:true})
class KeyFindings extends Component {
  render() {
    return (
      <div styleName='key-findings-wrap' id='key-findings'>
        <ContainerDivs>
          <h2>Key findings</h2>
          <Row>
            <Col xs={12} sm={12} md={6} lg={6}>
              <div styleName='key-findings-col-left'>
                <p>
                  As many banks start to limit their lending to carbon
                  intensive industries, companies in the coal, oil, and gas
                  value chain are turning more to the bond market to access
                  cheap debt.
                  Every time retirement plan participants
                  invest in fossil fuel corporate bonds, they are helping
                  new coal plants, new oil and gas pipelines, and other new
                  fossil fuel infrastructure get built.
                </p>
              </div>
            </Col>
            <Col xs={12} sm={12} md={6} lg={6}>
              <div styleName='key-findings-col-right'>
                <BulletAccordion bullets={[
                  [
                    'Invested in fossil fuel bonds by default',
                    'Large diversified target date fund series are often the default option in employer-offered retirement plans. These fund series generally hold many fossil fuel corporate bonds and aren\'t reducing exposure on the basis of climate risk.'
                  ],
                  [
                    'Few climate-safe bond fund options',
                    'Some plans offer treasury or other government bond fund options, but few offer sustainable diversified bond funds.'
                  ],
                  [
                    'Employees closer to retirement are more at risk of fossil fuel bond exposure',
                    'As people get closer to retirement, their allocation usually becomes more bond-heavy, meaning their risk of investing in fossil fuel bonds rises.'
                  ],
                  [
                    'Higher fossil fuel concentrations in bonds than stocks',
                    'Retirement plans generally have significantly more invested in stocks than bonds, but within the equity and corporate bond asset classes, fossil fuel concentrations are higher for bonds. For most of the plans analyzed, upwards of 15% of corporate bond holdings were fossil fuel.'
                  ],
                ]}/>
              </div>
            </Col>
          </Row>
          <ScrollToTop color='red'/>
        </ContainerDivs>
      </div>
    )
  }
}


/**
 * <BulletAccordion> component
 * Layout and state management for bullet accordion
 * @param bullets
 */
@CSSModules(Styles, {allowMultiple:true})
class BulletAccordion extends Component {
  static propTypes = {
    /**
     * List of bullets and slidedown content. Each item in list
     * is a list of two items, first item is bullet content,
     * second item is slidedown content.
     */
    bullets: PropTypes.array.isRequired
  }
  constructor(props) {
    super(props)
    this.state = {
      /** Number tracking which bullet point is open */
      open: null
    }
  }
  /**
   * Open a particular bullet point slidedown.
   *
   * @param {Number} [i=null] Index of the bullet to open (or close if
   *                          already open).
   */
  openBullet(i = null) {
    if (this.state.open !== i) {
      this.setState({ open: i })
    } else {
      this.setState({ open: null })
    }
  }
  //
  render() {
    if (!this.props.bullets) { return null }
    //
    return (
      <div styleName='fa-wrap'>
        {
          this.props.bullets.map((bullet, bulletIdx) => {
            const k = 'findings-accordion-bullet-' + bulletIdx
            return (
              <div key={k} styleName='fa-item-wrap'>
                <div
                  styleName='fa-bullet'
                  onClick={() => { this.openBullet(bulletIdx) }}
                >
                  <div styleName='fa-bullet-text'>{bullet[0]}</div>
                  <div>
                    <LinkIcon faclass={
                      this.state.open === bulletIdx ? 'fa-close' : 'fa-plus'
                    } nospace/>
                  </div>
                </div>
                <SlideDown>
                  { this.state.open === bulletIdx ?
                      <div styleName='fa-slidedown'>{bullet[1]}</div> :
                      null }
                </SlideDown>
              </div>
            )
          })
        }
      </div>
    )
  }
}


//
// <PlanSection> component
// Layout for plan section
// Display: some text and callout quotes
// Includes: <PlanSectionExposureConcentrationChart>
//           <PlanSelect>
//           <PlanBondFundsTableLayout>
//
@CSSModules(Styles, {allowMultiple:true})
class PlanSection extends Component {
  render() {
    const iconPlans = require( '../../../static/images/icon-plans.png' )
    //
    return (
      <div id='retirement-plans'>
        <ContainerDivs>
          <div styleName='plan-select-wrap'>
            <div styleName='plan-select-flex'>
              <div>
                <h2>Retirement plans</h2>
                <p>
                  While retirement plans have lower <b>exposure</b> to fossil fuel
                  bonds than fossil fuel stocks,
                  the <b>concentration</b> of fossil fuels is higher within
                  corporate bond holdings than within stocks.
                </p>
              </div>
              <img
                styleName='plan-select-img'
                src={iconPlans}
                alt='Retirement plans - 401(k)s & 403(b)s'
              />
            </div>
            <h3 styleName='plan-select-concentration-summary'>
              In U.S. 401(k)s
              and similar retirement plans,
              any given dollar invested in corporate bonds
              is almost <span>twice as likely to be fossil fuel</span> compared
              to a dollar invested in stocks.
            </h3>
            <PlanSectionExposureConcentrationChart />
            <h3 styleName='plan-select-aggregate-summary'>
              With $9.3 trillion invested in U.S. 401(k)s and similar
              retirement plans,
              an average fossil fuel bond exposure of 0.5% would amount to
              an estimated <span>$46.5 billion invested in fossil fuel bonds</span> by
              U.S. defined contribution plan participants.
            </h3>
            <PlanSelect />
            <PlanBondFundsTableLayout />
          </div>
          <ScrollToTop color='red'/>
        </ContainerDivs>
      </div>
    )
  }
}


//
// <PlanSectionExposureConcentrationChart> component
// Display: exposure + concentration chart,
//          exposure + concentration explainers,
//          exposure + concentration table
//
@CSSModules(Styles, {allowMultiple:true})
class PlanSectionExposureConcentrationChart extends Component {
  constructor(props) {
    super(props)
    this.state = {
      avgExposureBond: 0.005,
      avgExposureEquity: 0.062,
      avgConcentrationBond: 0.15,
      avgConcentrationEquity: 0.086,
    }
  }
  //
  render() {
    const exposureConcentrationChart = require( '../../../static/images/Fossil fuel stocks and bonds in 401ks.png' )
    //
    return (
      <div styleName='plan-select-exposure-concentration-chart-flex'>
        <img
          styleName='plan-select-exposure-concentration-chart'
          src={exposureConcentrationChart}
          alt='Fossil fuel stocks and bonds in 401(k)s'
        />
        <div styleName='plan-select-exposure-concentration-chart-disclaimer'>
          <p>
            <b>Exposure</b>: The percentage of plan assets invested in fossil
            fuel bonds or fossil fuel stocks. Most plans have over 10
            times more invested in fossil fuel stocks than fossil fuel bonds.
          </p>
          <p>
            <b>Concentration</b>: The percentage of plan assets
            classified as fossil fuel
            within each asset class.
            In other words,
            for most retirement plans,
            a dollar invested in corporate bonds is about twice as
            likely to be fossil fuel compared to any given dollar
            invested in stocks.
          </p>
          <p>
            Figures in the chart and table represent the average of the 43
            retirement plans analyzed below.
          </p>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Bond</th>
                <th>Equity</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Avg. fossil fuel exposure</td>
                <td>{fp(this.state.avgExposureBond)}</td>
                <td>{fp(this.state.avgExposureEquity)}</td>
              </tr>
              <tr>
                <td>Avg. fossil fuel concentration</td>
                <td>{fp(this.state.avgConcentrationBond)}</td>
                <td>{fp(this.state.avgConcentrationEquity)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    )
  }
}


//
// <PlanSelect> component
// Transforms config.plansBondsSource into a planData object
// Includes: <SelectDropdownPlan> and onPlanSelect()
//             to select an active plan,
//           <PlanResults> to display results
//             for active plan
//
@CSSModules(Styles, {allowMultiple:true})
class PlanSelect extends Component {
  constructor(props) {
    super(props)
    const plansData = Object.keys(config.plansBondsSource).map((key, i) => {
      const a = config.plansSource[key]
      const b = config.plansBondsSource[key]
      return {
        plan_option_count: a.plan_option_count,
        plan_assets: a.plan_assets,
        primary_plan_name: a.primary_plan_name,
        short_name: a.short_name,
        logo: require(
          `../../../static/images/plans/${a.path}.png`
        ),
        key: key,
        // from plansBondsSource
        unmatched_funds_count: b.unmatched_funds_count,
        unmatched_fund_assets: b.unmatched_fund_assets,
        unmatched_fund_weight: b.unmatched_fund_weight,
        matched_funds_count: b.matched_funds_count,
        matched_fund_assets: b.matched_fund_assets,
        matched_fund_weight: b.matched_fund_weight,
        long_equity_assets: b.long_equity_assets,
        long_cbond_assets: b.long_cbond_assets,
        ff_all_asset_bond: b.ff_all_asset_bond,
        ff_all_weight_bond: b.ff_all_weight_bond,
        ff_all_weight_bond_concentration: b.ff_all_weight_bond_concentration,
        ff_all_asset_equity: b.ff_all_asset_equity,
        ff_all_weight_equity: b.ff_all_weight_equity,
        ff_all_weight_equity_concentration: b.ff_all_weight_equity_concentration,
        ff_all_asset_both: b.ff_all_asset_equity_and_bond,
        ff_all_weight_both: b.ff_all_weight_equity_and_bond,
        top_cbond_companies_in_plan: b.top_cbond_companies_in_plan,
        // from planOptionsBondsSource
        plan_options_table: config.planOptionsBondsSource[key]
      }
    })
    this.state = {
      plansData,
      planSelected: {
        name: 'Select a plan',
        label: 'Select a plan'
      },
      planLoading: false,
      planTimeout: null,
    }
  }

  onPlanSelect = (val) => {
    // clear the timeout
    // this is in case user changes select before prev
    // timeout has finished
    if (this.state.planTimeout) {
      clearTimeout(this.state.planTimeout)
    }
    const timeoutLen = 500
    const scrollOffset = -70
    const scrollDuration = 500
    if (val) {
      // if we selected a value:
      // set the state so that the selected value is on the select
      // so that the loading icon is showing
      // so that nothing is open
      // and start a timeout for 1 second
      // after 1 second, hide the loading icon
      // and open the results
      scroller.scrollTo('scroll-to', {
        duration: scrollDuration,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: scrollOffset
      });
      this.setState({
        planSelected: val,
        planLoading: true,
        planTimeout: setTimeout(()=>{
          this.setState({
            planLoading: false,
          })
        }, timeoutLen)
      })
    } else {
      // if didn't select a value
      // set selected value to default option
      // set loading icon to off
      // nothing should be showing
      // and there is no timeout.
      scroller.scrollTo('scroll-to', {
        duration: scrollDuration,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: scrollOffset
      });
      this.setState({
        planSelected: { name: 'Select a plan', label: 'Select a plan' },
        planLoading: true,
        planTimeout: setTimeout(()=>{
          this.setState({
            planLoading: false,
          })
        }, timeoutLen)
      })
    }
  }

  render() {
    // select active plan
    let activePlan = null
    if (this.state.planSelected.name !== 'Select a plan') {
      activePlan = this.state.plansData.find(
        (plan) => { return this.state.planSelected.name === plan.key }
      )
    }
    //
    return (
      <div>
        <p>
          Use the dropdown or click a row on the table
          to see details on
          how much that retirement
          plan has invested
          in fossil fuel bonds and stocks, as well as
          details on each plan option.
        </p>
        <div id='scroll-to'/>
        <SelectDropdownPlan
          plans={config.plansBondsSource}
          value={this.state.planSelected}
          onChange={this.onPlanSelect}
        />
        <PlanResults
          planLoading={this.state.planLoading}
          plansData={this.state.plansData}
          activePlan={activePlan}
          onPlanSelect={this.onPlanSelect}
        />
      </div>
    )
  }
}


//
// <PlanResults> component
//
// @param planLoading:
// @param activePlan:
// @param plansData:
// @param onPlanSelect:
//
@CSSModules(Styles, {allowMultiple:true})
class PlanResults extends Component {
  render() {
    return (
      <div>
        { /* LOADING ICON DURING PLAN SELECT TIMEOUT */
          this.props.planLoading ?
            <div styleName='plan-loader-wrap'>
              <Loader
                options={{ scale: 0.70, top: '50px' }}
                loaded={false}
              />
            </div> :
            null
        }

        {
          /* LIST OF PLANS*/
          !this.props.planLoading && !this.props.activePlan ?
            (
              <div styleName='plan-list'>
                {
                  this.props.plansData.map((plan, planI) => {
                    return (
                      <div key={'plan-list-row-'+planI}>
                        {
                          !(planI % 10) ?
                            (
                              <div styleName='plan-list-row-header'>
                                <div styleName='plan-list-row-header-row-header'>
                                  <div>
                                    &nbsp;
                                  </div>
                                  <div>
                                    <b>Fossil fuel bonds</b>
                                  </div>
                                </div>
                                <div styleName='plan-list-row-header-row'>
                                  <div>
                                    <b>Employer</b>
                                  </div>
                                  <div>
                                    <i>Investments</i>
                                  </div>
                                  <div>
                                    <i>Exposure</i>
                                  </div>
                                  <div>
                                    <i>Concentration</i>
                                  </div>
                                </div>
                              </div>
                            ) :
                            null
                        }
                        <div
                          styleName='plan-list-row'
                          key={'plan-select-list-'+planI}
                          onClick={() => {
                            this.props.onPlanSelect({
                              name: plan.key,
                              label: plan.short_name
                            })
                          }}
                        >
                          <div styleName='plan-list-logo-wrap'>
                            <img
                              src={plan.logo}
                              alt={plan.short_name}
                              styleName='plan-list-logo'
                            />
                            <SrOnly>{plan.short_name}</SrOnly>
                          </div>
                          <div>
                            { fm(plan.ff_all_asset_bond) }
                          </div>
                          <div>
                            { fp(plan.ff_all_weight_bond) }
                          </div>
                          <div>
                            <span styleName='red'>
                              { fp(plan.ff_all_weight_bond_concentration) }
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })
                }
              </div>
            ) :
            null
        }

        { /* PLAN RESULTS */
          !this.props.planLoading && this.props.activePlan ?
            (
              <div styleName='plan-summary-wrap' id={this.props.activePlan.key}>
                <div
                  styleName='plan-summary-back-to-list'
                  onClick={() => {
                    this.props.onPlanSelect({
                      name: 'Select a plan',
                      label: 'Select a plan'
                    })
                  }}
                >
                  <LinkIcon nospace faclass='fa-arrow-left'/>
                  &nbsp;
                  Back to list
                </div>
                <PlanSummary activePlan={this.props.activePlan}/>
                <PlanCharts activePlan={this.props.activePlan}/>
                <PlanOptionsTableLayout activePlan={this.props.activePlan}/>
                <div
                  styleName='plan-summary-back-to-list'
                  onClick={() => {
                    this.props.onPlanSelect({
                      name: 'Select a plan',
                      label: 'Select a plan'
                    })
                  }}
                >
                  <LinkIcon nospace faclass='fa-arrow-left'/>
                  &nbsp;
                  Back to list
                </div>
              </div>
            ) :
            null
        }
      </div>
    )
  }
}


//
// <PlanSummary> component
// Layout for plan summary, including desktop/tablet and mobile
// Includes: <PlanSummaryLogo>,
//           <PlanSummaryNameSize>,
//           <PlanSummaryTopFossilFuelBondHoldings>,
//           <PlanSummaryFossilFuelInvestments>
// @param activePlan: Object of active plan from <PlanSelect>
//
@CSSModules(Styles, {allowMultiple:true})
class PlanSummary extends Component {
  render() {
    if (!this.props.activePlan) { return null }
    return (
      <div>
        <Row>
          <Col xs={12} sm={6}>
            <PlanSummaryLogo activePlan={this.props.activePlan}/>
          </Col>
          <Col xs={12} sm={6}>
            <PlanSummaryNameSize activePlan={this.props.activePlan}/>
          </Col>
        </Row>
        <div styleName='hidden-xs'>
          <Row>
            <Col sm={6}>
              <PlanSummaryTopFossilFuelBondHoldings activePlan={this.props.activePlan}/>
            </Col>
            <Col sm={6}>
              <PlanSummaryFossilFuelInvestments activePlan={this.props.activePlan}/>
            </Col>
          </Row>
        </div>
        <div styleName='visible-xs'>
          <Row>
            <Col xs={12} sm={0}>
              <PlanSummaryFossilFuelInvestments activePlan={this.props.activePlan}/>
            </Col>
            <Col xs={12} sm={0}>
              <PlanSummaryTopFossilFuelBondHoldings activePlan={this.props.activePlan}/>
            </Col>
          </Row>
        </div>
      </div>
    )
  }
}


//
// <PlanSummaryLogo> component
// Display: image of plan logo
// @param activePlan: Object of active plan from <PlanSelect>
//
@CSSModules(Styles, {allowMultiple:true})
class PlanSummaryLogo extends Component {
  render() {
    if (!this.props.activePlan) { return null }
    return (
      <div styleName='plan-summary-col'>
        <h3 styleName='plan-summary-section-heading'>
          EMPLOYER
        </h3>
        <img
          src={this.props.activePlan.logo}
          alt={this.props.activePlan.short_name}
          styleName='plan-summary-logo'
        />
      </div>
    )
  }
}


//
// <PlanSummaryNameSize> component
// Display: name, size of plan
// @param activePlan: Object of active plan from <PlanSelect>
//
@CSSModules(Styles, {allowMultiple:true})
class PlanSummaryNameSize extends Component {
  constructor(props) { super(props) }
  render() {
    if (!this.props.activePlan) { return null }
    return (
      <div>
        <div styleName='plan-summary-col'>
          <h3 styleName='plan-summary-section-heading'>
            PLAN NAME
          </h3>
          <p>
            {this.props.activePlan.primary_plan_name}
          </p>
        </div>
        <div styleName='plan-summary-col'>
          <h3 styleName='plan-summary-section-heading'>
            PLAN SIZE
          </h3>
          <p>
            { fm(this.props.activePlan.plan_assets) } invested
            in {this.props.activePlan.plan_option_count} plan options
          </p>
        </div>
      </div>
    )
  }
}


//
// <PlanSummaryTopFossilFuelBondHoldings> component
// Display: table of top fossil fuel bond holdings
// @param activePlan: Object of active plan from <PlanSelect>
//
@CSSModules(Styles, {allowMultiple:true})
class PlanSummaryTopFossilFuelBondHoldings extends Component {
  render() {
    if (!this.props.activePlan) { return null }
    return (
      <div styleName='plan-summary-col'>
        <h3 styleName='plan-summary-section-heading'>
          TOP FOSSIL FUEL BOND HOLDINGS
        </h3>
        <p>
          Top fossil fuel corporate bond issuers
          within plan options
        </p>
        <div styleName='top-companies-ol'>
          {
            this.props.activePlan.top_cbond_companies_in_plan.map((c, i2)=>{
              return (
                <div
                  key={'top_cbond_companies_'+this.props.activePlan.key+'_'+i2}
                  styleName={'top-companies-li'}
                >
                  <div styleName='top-companies-li-flex'>
                    <div>{i2 + 1}.</div>
                    <div>{c[0]}</div>
                    <div>{fm(c[1])}</div>
                  </div>
                </div>
              )
            })
          }
        </div>
      </div>
    )
  }
}


@CSSModules(Styles, {allowMultiple:true})
class PlanSummaryFossilFuelInvestments extends Component {
  constructor(props) { super(props) }
  render() {
    if (!this.props.activePlan) { return null }
    return (
      <div styleName='fossil-fuel-investments-summary-wrap'>
        <div styleName='plan-summary-heading-col'>
          <h3 styleName='plan-summary-section-heading'>
            FOSSIL FUEL INVESTMENTS
          </h3>
        </div>
        <div styleName='plan-summary-col'>
          <p styleName='plan-summary-data-section-heading'>
            <i>CORPORATE BONDS</i>
          </p>
          <div styleName='plan-summary-stat-flex'>
            <div>
              Investments
            </div>
            <div>
              { fm(this.props.activePlan.ff_all_asset_bond) }
            </div>
          </div>
          <div styleName='plan-summary-stat-flex'>
            <div>
              Exposure
              <div>Out of { fm(this.props.activePlan.plan_assets)
              } invested in all plan options</div>
            </div>
            <div>
              { fp(this.props.activePlan.ff_all_weight_bond) }
            </div>
          </div>
          <div styleName='plan-summary-stat-flex'>
            <div>
              Concentration
              <div>Out of { fm(this.props.activePlan.long_cbond_assets)
              } invested in corporate bonds</div>
            </div>
            <div>
              <span styleName='red'>{ fp(this.props.activePlan.ff_all_weight_bond_concentration) }</span>
            </div>
          </div>
          <p styleName='plan-summary-data-section-heading'>
            <i>EQUITIES (STOCKS)</i>
          </p>
          <div styleName='plan-summary-stat-flex'>
            <div>
              Investments
            </div>
            <div>
              { fm(this.props.activePlan.ff_all_asset_equity) }
            </div>
          </div>
          <div styleName='plan-summary-stat-flex'>
            <div>
              Exposure
              <div>Out of { fm(this.props.activePlan.plan_assets)
              } invested in all plan options</div>
            </div>
            <div>
              { fp(this.props.activePlan.ff_all_weight_equity) }
            </div>
          </div>
          <div styleName='plan-summary-stat-flex'>
            <div>
              Concentration
              <div>Out of { fm(this.props.activePlan.long_equity_assets)
              } invested in stocks</div>
            </div>
            <div>
              <span styleName='red'>{ fp(this.props.activePlan.ff_all_weight_equity_concentration) }</span>
            </div>
          </div>
        </div>
      </div>
    )
  }
}


@CSSModules(Styles, {allowMultiple:true})
class PlanCharts extends Component {
  constructor(props) {
    super(props)
    const { activePlan } = this.props
    const urlPrefix = 'https://iyv-charts.s3-us-west-2.amazonaws.com/fossil_fuel_bonds/'
    const planWeightChartUrl = `${urlPrefix}${activePlan.primary_plan_name} fossil fuel bonds weight ${currYYYYMM}.png`
    const planWeightChartAlt = `${activePlan.primary_plan_name} weight chart`
    const planAssetChartUrl = `${urlPrefix}${activePlan.primary_plan_name} fossil fuel bonds assets ${currYYYYMM}.png`
    const planAssetChartAlt = `${activePlan.primary_plan_name} assets chart`
    this.state = {
      isAssetChart: null,
      planWeightChartUrl,
      planWeightChartAlt,
      planAssetChartUrl,
      planAssetChartAlt,
    }
  }
  render() {
    return (
      <div styleName='plan-charts-wrap'>
        <Row>
          <Col xs={12} sm={8}>
            <h3 styleName='plan-summary-section-heading'>
              PLAN INVESTMENT OPTIONS
            </h3>
            <p>
              Here is a list of the funds
              offered by this employer's plan.
              You can sort by the fund's percent
              exposure to fossil fuels, or
              the amount plan participants have invested in
              fossil fuels via that plan option.
            </p>
          </Col>
          <Col xs={12} sm={4}>
            <div styleName='chart-button-flex-wrap'>
              <div styleName='chart-button-flex-cell'>
                <div styleName='chart-button-label-flex'>
                  <i>Chart type</i>
                </div>
                <div styleName='chart-button-flex'>
                  <label styleName="toggle">
                    <span styleName='toggle-label'>Percent</span>
                    <input
                      styleName='toggle-checkbox'
                      type='checkbox'
                      checked={this.state.isAssetChart}
                      onClick={()=>{
                        this.setState({
                          isAssetChart: !this.state.isAssetChart
                        })
                      }}
                    />
                    <div styleName='toggle-switch'></div>
                    <span styleName='toggle-label'>USD $</span>
                  </label>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        { /* CHART IMAGE 1 - BY WEIGHT */
          !this.state.isAssetChart ?
            (
              <div>
                <p styleName='chart-disclaimer'>
                  <LinkIcon nospace faclass='fa-info-circle'/>
                  &nbsp;This chart shows the percent of each
                  plan option that is invested in fossil
                  fuel bonds and stocks. The plan options
                  at the top of the chart have the most
                  exposure to fossil fuels,
                  and the plan options at the bottom of
                  the chart have the least exposure.
                </p>
                <div styleName='chart-img-wrap'>
                  <Link
                    to={this.state.planWeightChartUrl}
                    title={this.state.planWeightChartAlt}
                    targetBlank
                  >
                    <img
                      src={this.state.planWeightChartUrl}
                      alt={this.state.planWeightChartAlt}
                      styleName='chart-img'
                    />
                    <span styleName='chart-expand-label visible-xs'>
                      Click to enlarge<LinkIcon faclass='fa-expand'/>
                    </span>
                  </Link>
                </div>
              </div>
            ) :
            /* CHART IMAGE 2 - BY ASSET */
            (
              <div styleName='chart-img-wrap'>
                <p styleName='chart-disclaimer'>
                  <LinkIcon nospace faclass='fa-info-circle'/>
                  &nbsp;This chart shows, for each plan option
                  in the retirement plan,
                  the amount in USD invested
                  in fossil fuel bonds and stocks
                  by plan participants via that fund.
                  The plan options at the top of
                  the chart account for the most money
                  invested in fossil fuel bonds and stocks
                  by plan participants, and the plan options
                  at the bottom of the chart account
                  for the least.
                  In total, plan participants have {
                    fm(this.props.activePlan.ff_all_asset_both)
                  } invested in fossil fuels via these
                  plan options.
                </p>
                <div styleName='chart-img-wrap'>
                  <Link
                    to={this.state.planAssetChartUrl}
                    title={this.state.planAssetChartAlt}
                    targetBlank
                  >
                    <img
                      src={this.state.planAssetChartUrl}
                      alt={this.state.planAssetChartAlt}
                      styleName='chart-img'
                    />
                    <span styleName='chart-expand-label visible-xs'>
                      Click to enlarge<LinkIcon faclass='fa-expand'/>
                    </span>
                  </Link>
                </div>
              </div>
            )
        }
      </div>
    )
  }
}


@CSSModules(Styles, {allowMultiple:true})
class PlanOptionsTableLayout extends Component {
  render() {
    if (!this.props.activePlan) { return null }
    return (
      <div styleName='plan-options-table-wrap'>
        <h3 styleName='plan-summary-section-heading'>
          PLAN INVESTMENT OPTIONS TABLE
        </h3>
        <div styleName='hidden-xs hidden-sm'>
          <PlanOptionsTableDesktop activePlan={this.props.activePlan}/>
        </div>
        <div styleName='hidden-md hidden-lg'>
          <PlanOptionsTableMobile activePlan={this.props.activePlan}/>
        </div>
      </div>
    )
  }
}


@CSSModules(Styles, {allowMultiple:true})
class PlanOptionsTableDesktop extends Component {
  render() {
    if (!this.props.activePlan) { return null }
    const { activePlan } = this.props
    return (
      <table styleName='plan-options-table'>
        { /* FIRST THEAD */ }
        <thead>
          <tr>
            <th colSpan='2' />
            <th colSpan='3'>
              <i>Fossil fuel exposure</i>
            </th>
            <th colSpan='3'>
              <i>Fossil fuel plan investments</i>
            </th>
            <th />
          </tr>
          <tr>
            { /* THEAD SECOND TR */
              [
                'Plan option',
                'Rated as',
                'Bond',
                'Equity',
                'Total',
                'Bond',
                'Equity',
                'Total',
                'Total plan investments'
              ].map((th, th_i) => {
                const k = 'plan-option-table-th-'+th_i
                return <th key={k}>{th}</th>
              })
            }
          </tr>
        </thead>
        <tbody>
          <tr styleName='table-total-row'>
            { /* TOTAL ROW FOR MATCHED FUNDS */
              [
                'All matched funds',
                null,
                fp(activePlan.ff_all_weight_bond),
                fp(activePlan.ff_all_weight_equity),
                fp(activePlan.ff_all_weight_both),
                fm(activePlan.ff_all_asset_bond),
                fm(activePlan.ff_all_asset_equity),
                fm(activePlan.ff_all_asset_both),
                fm(activePlan.matched_fund_assets)
              ].map((td, td_i) => {
                const k = 'plan-option-table-matched-totals-'+td_i
                return <td key={k}>
                  <b>{td}</b>
                </td>
              })
            }
          </tr>
          { /* MATCHED PLAN OPTIONS AS ROWS */
            /* FOR EACH PLAN OPTION... */
            activePlan.plan_options_table.map((o, i3)=>{
              const [plan_option_name,
                     ticker,
                     fund_id,
                     investment_vehicle_id,
                     market_value,
                     ff_all_weight_equity,
                     ff_all_asset_equity,
                     ff_all_weight_bond,
                     ff_all_asset_bond,
                     ff_all_weight_both,
                     ff_all_asset_both] = o
              // IF IT HAS NO FUND_ID, SKIP IT
              if (fund_id==='~') { return null }
              // IF FUND_ID, IT'S MATCHED
              // RETURN A ROW WITH MORE DATA
              const _k = 'plan-option-table-'+
                'matched-option-'+i3
              return (
                <tr key={_k} styleName='plan-option-table-row'>
                  { /* RETURN A <TD> WITH DATA */
                    [
                      plan_option_name,
                      ticker,
                      fp(ff_all_weight_bond),
                      fp(ff_all_weight_equity),
                      fp(ff_all_weight_both),
                      fm(ff_all_asset_bond),
                      fm(ff_all_asset_equity),
                      fm(ff_all_asset_both),
                      fm(market_value),
                    ].map((td, td_i) => {
                      const k = 'plan-option-table-'+
                        'matched-option-'+i3+'-'+td_i
                      return <td key={k}>{td}</td>
                    })
                  }
                </tr>
              )
            })
          }

          { /* UNMATCHED FUNDS */ }
          <tr styleName='table-disclaimer-row'>
            <th colSpan='9'>
              <div styleName='table-disclaimer'>
                <b><LinkIcon nospace faclass='fa-exclamation-triangle'/>&nbsp;Unmatched funds</b>
                <br/>
                {
                  activePlan.unmatched_funds_count
                } plan options were not matched to any
                mutual fund, with {
                  fm(activePlan.unmatched_fund_assets)
                } invested by plan participants,
                making up {
                  fp(activePlan.unmatched_fund_weight)
                } of plan assets.
                Unmatched plan options are things like
                employee stock ownership funds,
                money market funds, and stable value funds,
                but could also include separately managed accounts where
                there is little or no transparency into fund
                holdings. <b>These plan options may
                include
                fossil fuel investments that aren't
                accounted for in the figures above.</b>
              </div>
            </th>
          </tr>
          { /* TOTAL ROW FOR UNMATCHED FUNDS */ }
          <tr styleName='table-total-row'>
            <td colSpan='8'>
              <b>All unmatched funds</b>
            </td>
            <td>
              <b>{fm(activePlan.unmatched_fund_assets)}</b>
            </td>
          </tr>
          { /* UNMATCHED PLAN OPTIONS AS ROWS 1 */
            /* FOR EACH PLAN OPTION... */
            activePlan.plan_options_table.map((o, i4)=>{
              console.log('hi here')
              console.log(o)
              const plan_option_name = o[0]
              const fund_id = o[2]
              const market_value = o[4]
              // IF IT HAS A FUND_ID, SKIP IT
              if (fund_id!=='~') {
                return null
              }
              // IF NO FUND_ID, IT'S UNMATCHED
              // RETURN A ROW WITH LESS DATA
              const _k = 'plan-option-table-'+
                'unmatched-option-'+i4
              return (
                <tr key={_k} styleName='plan-option-table-row'>
                  <td colSpan='8'>{plan_option_name}</td>
                  <td>{fm(market_value)}</td>
                </tr>
              )
            })
          }
        </tbody>
      </table>
    )
  }
}


@CSSModules(Styles, {allowMultiple:true})
class PlanOptionsTableMobile extends Component {
  constructor(props) {
    super(props)
    this.state = {}
  }
  opener(rowLabel) {
    const newState = {}
    newState[rowLabel] = !this.state[rowLabel]
    this.setState(newState)
  }
  render() {
    if (!this.props.activePlan) { return null }
    const { activePlan } = this.props
    return (
      <div styleName='plan-options-table-responsive-wrap'>
        { /* Plan option >> Fossil fuel bond exposure */ }
        <div styleName='plan-options-table-responsive-row responsive-header-row'>
          <div>
           Plan option
          </div>
          <div>
           Fossil fuel bond exposure
          </div>
          <div/>
        </div>
        { /* All Matched Funds >> Bond Exposure % >> More... */ }
        <div
          styleName='plan-options-table-responsive-button-row responsive-total-row'
          onClick={ () => { this.opener('all-matched-funds') } }
        >
          <div>
            All matched funds
          </div>
          <div styleName='responsive-figure'>
            { fp(activePlan.ff_all_weight_bond) }
          </div>
          <div>
            <LinkIcon nospace faclass='fa-ellipsis-v'/>
          </div>
        </div>
        <SlideDown>
          {
            this.state['all-matched-funds'] ?
              <div styleName='plan-options-table-responsive-more-results-row' >
                {
                  [
                    ['Fossil fuel exposure', null, 0, 1],
                    ['Bond', fp(activePlan.ff_all_weight_bond), 1, 0],
                    ['Equity', fp(activePlan.ff_all_weight_equity), 1, 0],
                    ['Total', fp(activePlan.ff_all_weight_both), 1, 0],
                    ['Fossil fuel investments', null, 0, 1],
                    ['Bond', fm(activePlan.ff_all_asset_bond), 1, 0],
                    ['Equity', fm(activePlan.ff_all_asset_equity), 1, 0],
                    ['Total', fm(activePlan.ff_all_asset_both), 1, 0],
                    ['Total plan investments', fm(activePlan.matched_fund_assets), 1, 0],
                  ].map((oLblVal, i7) => {
                    const [lbl, val, isFigure, isItalic] = oLblVal
                    return (
                      <div
                        styleName='responsive-more-results-data-row'
                        key={'all-matched-funds-responsive-more-results-data-row-'+i7}
                      >
                        <div styleName={isItalic ? 'responsive-italic' : ''}>
                          {lbl}
                        </div>
                        <div styleName={isFigure ? 'responsive-figure' : ''}>
                          {val}
                        </div>
                      </div>
                    )
                  })
                }
              </div> :
              null
          }
        </SlideDown>
        { /* Plan option name >> Bond Exposure % >> More... */ }
        { /* MATCHED PLAN OPTIONS AS ROWS */
          /* FOR EACH PLAN OPTION... */
          activePlan.plan_options_table.map((o, i5)=>{
            const [plan_option_name,
                   ticker,
                   fund_id,
                   market_value,
                   ff_all_weight_equity,
                   ff_all_asset_equity,
                   ff_all_weight_bond,
                   ff_all_asset_bond,
                   ff_all_weight_both,
                   ff_all_asset_both] = o
            // IF IT HAS NO FUND_ID, SKIP IT
            if (fund_id==='~') { return null }
            // IF FUND_ID, IT'S MATCHED
            // RETURN A ROW WITH MORE DATA
            const k = 'responsive-table-option-matched-'+i5
            return (
              <div key={k}>
                <div
                  styleName='plan-options-table-responsive-button-row'
                  onClick={ () => { this.opener('matched-'+i5) } }
                >
                  <div>
                    {plan_option_name}
                  </div>
                  <div styleName='responsive-figure'>
                    { fp(ff_all_weight_bond) }
                  </div>
                  <div>
                    <LinkIcon nospace faclass='fa-ellipsis-v'/>
                  </div>
                </div>
                <SlideDown>
                  {
                    this.state['matched-'+i5] ?
                      <div styleName='plan-options-table-responsive-more-results-row' >
                        {
                          [
                            ['Rated as', ticker, 0, 0],
                            ['Fossil fuel exposure', null, 0, 1],
                            ['Bond', fp(ff_all_weight_bond), 1, 0],
                            ['Equity', fp(ff_all_weight_equity), 1, 0],
                            ['Total', fp(ff_all_weight_both), 1, 0],
                            ['Fossil fuel investments', null, 0, 1],
                            ['Bond', fm(ff_all_asset_bond), 1, 0],
                            ['Equity', fm(ff_all_asset_equity), 1, 0],
                            ['Total', fm(ff_all_asset_both), 1, 0],
                            ['Total plan investments', fm(market_value), 1, 0],
                          ].map((oLblVal, i6) => {
                            const [lbl, val, isFigure, isItalic] = oLblVal
                            return (
                              <div
                                styleName='responsive-more-results-data-row'
                                key={'plan-option-responsive-more-results-data-row-'+i6}
                              >
                                <div styleName={isItalic ? 'responsive-italic' : ''}>
                                  {lbl}
                                </div>
                                <div styleName={isFigure ? 'responsive-figure' : ''}>
                                  {val}
                                </div>
                              </div>
                            )
                          })
                        }
                      </div> :
                      null
                  }
                </SlideDown>
              </div>
            )
          })
        }
        <div styleName='plan-options-table-responsive-row responsive-header-row'>
          <div>
            <div styleName='table-disclaimer'>
              <b><LinkIcon nospace faclass='fa-exclamation-triangle'/>&nbsp;
              Unmatched funds</b>
              <br/>
              {
                activePlan.unmatched_funds_count
              } plan options were not matched to any
              mutual fund, with {
                fm(activePlan.unmatched_fund_assets)
              } invested by plan participants,
              making up {
                fp(activePlan.unmatched_fund_weight)
              } of plan assets. These plan options may
              include
              fossil fuel investments that aren't
              accounted for in the figures above.
            </div>
          </div>
        </div>
        { /* Plan option >> Fossil fuel bond exposure */ }
        <div styleName='plan-options-table-responsive-row responsive-header-row'>
          <div>
           Plan option
          </div>
          <div>
           Total plan investments
          </div>
          <div/>
        </div>
        { /* All Unatched Funds >>  */ }
        <div
          styleName='plan-options-table-responsive-button-row responsive-total-row'
        >
          <div>
            All unmatched funds
          </div>
          <div styleName='responsive-figure'>
            { fm(activePlan.unmatched_fund_assets) }
          </div>
          <div/>
        </div>
        { /* UNMATCHED PLAN OPTIONS AS ROWS 2 */
          /* FOR EACH PLAN OPTION... */
          activePlan.plan_options_table.map((o, i5)=>{
            const [plan_option_name,
                   ticker,
                   fund_id,
                   investment_vehicle_id,
                   market_value] = o
            // IF IT HAS NO FUND_ID, SKIP IT
            if (fund_id!=='~') { return null }
            // IF FUND_ID, IT'S MATCHED
            // RETURN A ROW WITH MORE DATA
            const k = 'responsive-table-option-matched-'+i5
            return (
              <div key={k}>
                <div
                  styleName='plan-options-table-responsive-button-row'
                  onClick={ () => { this.opener('matched-'+i5) } }
                >
                  <div>
                    {plan_option_name}
                  </div>
                  <div styleName='responsive-figure'>
                    { fm(market_value) }
                  </div >
                  <div />
                </div>
              </div>
            )
          })
        }
      </div>
    )
  }
}


@CSSModules(Styles, {allowMultiple:true})
class PlanBondFundsTableLayout extends Component {
  constructor(props) {
    super(props)
    this.state = {
      planBondFundsOpen: false,
      planBondFundsData: [
        ['Baird', 'Baird Core Plus Bond Fund', 0.493054, 0.072454, 0.146949],
        ['BlackRock/iShares','BlackRock Total Return Fund',0.247943,0.054641,0.220376],
        ['BlackRock/iShares','iShares Intermediate Government/Credit Bond ETF',0.326101,0.039468,0.121031],
        ['Dodge & Cox', 'Dodge & Cox Income Fund', 0.407703, 0.083717, 0.205337],
        ['Eaton Vance','Eaton Vance High Income Opportunities Fund',0.865161,0.133719,0.15456],
        ['Fidelity Investments','Fidelity® Capital & Income Fund',0.745349,0.129826,0.174181],
        ['Fidelity Investments','Fidelity® Government Income Fund',0.0014,0.0014,1.0],
        ['Fidelity Investments','Fidelity® Inflation-Protected Bond Index Fund',0.0,0.0,0.0],
        ['Fidelity Investments','Fidelity® Total Bond Fund',0.346875,0.058734,0.169324],
        ['Fidelity Investments','Fidelity® Total Bond K6 Fund',0.341398,0.070594,0.20678],
        ['Fidelity Investments','Fidelity® U.S. Bond Index Fund',0.251279,0.038861,0.154653],
        ['Hartford Mutual Funds','The Hartford World Bond Fund',0.084813,0.013107,0.154538],
        ['Metropolitan West Funds','Metropolitan West High Yield Bond Fund',0.827335,0.066153,0.07996],
        ['Metropolitan West Funds','Metropolitan West Total Return Bond Fund',0.251019,0.017325,0.069019],
        ['PGIM Investments','PGIM Total Return Bond Fund',0.242999,0.046194,0.190098],
        ['PIMCO', 'PIMCO Income Fund', 0.042801, 0.009435, 0.220438],
        ['PIMCO', 'PIMCO Inflation Response Multi-Asset Fund', 0.0, 0.0, 0.0],
        ['PIMCO', 'PIMCO Low Duration Fund', 0.104526, 0.009559, 0.091448],
        ['PIMCO', 'PIMCO Real Return Fund', 0.003019, 1.2e-05, 0.004134],
        ['PIMCO', 'PIMCO Total Return Fund', 0.113876, 0.012424, 0.109102],
        ['PIMCO', 'PIMCO Total Return II Fund', 0.17802, 0.048347, 0.271579],
        ['SPDR State Street Global Advisors','SPDR® Portfolio TIPS ETF',0.0,0.0,0.0],
        ['State Street Global Advisors','State Street Aggregate Bond Index Portfolio',0.242708,0.03708,0.152776],
        ['T. Rowe Price','T. Rowe Price International Bond Fund',0.107998,0.010501,0.097233],
        ['TCW', 'TCW Total Return Bond Fund', 0.00498, 0.0, 0.0],
        ['Vanguard', 'Vanguard GNMA Fund', 0.0, 0.0, 0.0],
        ['Vanguard','Vanguard High Yield Corporate Fund',0.87513,0.126553,0.14461],
        ['Vanguard', 'Vanguard Inflation-Protected Securities Fund', 0.0, 0.0, 0.0],
        ['Vanguard','Vanguard Intermediate-Term Bond Index Fund',0.382505,0.054052,0.141311],
        ['Vanguard','Vanguard Short Term Investment Grade Fund',0.841843,0.109238,0.12976],
        ['Vanguard','Vanguard Short-Term Bond Index Fund',0.282324,0.029389,0.104097],
        ['Vanguard','Vanguard Short-Term Inflation-Protected Securities Index Fund',0.0,0.0,0.0],
        ['Vanguard','Vanguard Total Bond Market Index Fund',0.271262,0.041361,0.152478],
        ['Vanguard','Vanguard Total International Bond Index Fund',0.113145,0.011646,0.102927],
        ['Vanguard', 'Vanguard Wellesley® Income Fund', 0.441257, 0.075057, 0.170098],
        ['Victory Capital', 'Victory INCORE Fund for Income', 0.0, 0.0, 0.0]
      ]
    }
  }

  render() {
    return (
      <div>
        <br/>
        <br/>
        <br/>
        <button
          styleName='section-slidedown-button'
          onClick={
            (e)=>{
              this.setState({
                planBondFundsOpen: !this.state.planBondFundsOpen
              })
            }
          }
        >
          <h2 styleName='section-slidedown-label'>
            See all plan bond funds
          </h2>
          <div styleName='section-slidedown-icon'>
            <LinkIcon faclass={ this.state.planBondFundsOpen ? 'fa-angle-up' : 'fa-angle-down' } />
          </div>
        </button>
        <SlideDown>
          {
            this.state.planBondFundsOpen ?
              (
                <div>
                  <p>
                    The table below lists the 36 bond funds found within
                    the 43 retirement plans analyzed.
                    None of the bond funds in these plans
                    were sustainable investments that seek to mitigate
                    climate risk to the portfolio.
                    Some of these bond funds invest solely in treasury bonds
                    or mortgage bonds, and have 0% exposure to the
                    corporate bond asset class, and thus have 0%
                    fossil fuel bond exposure and concentration.
                    Information
                    on the target date funds in these retirement
                    plans <ScrollLink
                      activeClass='active-scroll-link'
                      to='target-dates'
                      spy
                      smooth
                      offset={-70}
                      duration={500}
                    >
                      <span styleName='red-link'>
                        can be found below
                      </span>
                    </ScrollLink>.
                  </p>
                  <div styleName='hidden-xs hidden-sm'>
                    <PlanBondFundsTableDesktop
                      planBondFundsData={this.state.planBondFundsData}
                    />
                  </div>
                  <div styleName='hidden-md hidden-lg'>
                    <PlanBondFundsTableMobile
                      planBondFundsData={this.state.planBondFundsData}
                    />
                  </div>
                </div>
              ) :
              null
          }
        </SlideDown>
      </div>
    )
  }
}


@CSSModules(Styles, {allowMultiple:true})
class PlanBondFundsTableDesktop extends Component {
  constructor(props) {
    super(props)
  }
  render() {
    return (
      <table styleName='plan-options-table'>
        <thead>
          <tr>
            <th>Asset manager</th>
            <th>Fund name</th>
            <th>% of fund invested in corporate bonds</th>
            <th>% of fund invested in fossil fuel corporate bonds (exposure)</th>
            <th>% of corporate bond holdings labeled fossil fuel (concentration)</th>
          </tr>
        </thead>
        <tbody>
          {
            this.props.planBondFundsData.map((e, i)=> {
              const [fam, name, cbond_ex, ff_ex, ff_conc] = e
              return (
                <tr styleName='plan-option-table-row'>
                  <td>{fam}</td>
                  <td>{name}</td>
                  <td>{fp(cbond_ex)}</td>
                  <td>{fp(ff_ex)}</td>
                  <td>{fp(ff_conc)}</td>
                </tr>
              )
            })
          }
        </tbody>
      </table>
    )
  }
}


@CSSModules(Styles, {allowMultiple:true})
class PlanBondFundsTableMobile extends Component {
  constructor(props) {
    super(props)
    this.state = {}
  }
  opener(rowLabel) {
    const newState = {}
    newState[rowLabel] = !this.state[rowLabel]
    this.setState(newState)
  }
  render() {
    return (
      <div styleName='plan-options-table-responsive-wrap'>
        { /* Plan option >> Fossil fuel bond exposure */ }
        <div styleName='plan-options-table-responsive-row responsive-header-row'>
          <div>
            Bond fund
          </div>
          <div/>
        </div>
        { /* Plan option name >> Bond Exposure % >> More... */ }
        { /* MATCHED PLAN OPTIONS AS ROWS */
          /* FOR EACH PLAN OPTION... */
          this.props.planBondFundsData.map((e, i8)=>{
            const [fam, name, cbond_ex, ff_ex, ff_conc] = e
            const k = 'responsive-table-all-bond-funds-'+i8
            return (
              <div key={k} styleName='even-or-odd'>
                <div
                  styleName='plan-options-table-responsive-button-row'
                  onClick={ () => { this.opener('all-bond-funds-'+i8) } }
                >
                  <div>
                    {name}
                  </div>
                  <div>
                    <LinkIcon nospace faclass='fa-ellipsis-v'/>
                  </div>
                </div>
                <SlideDown>
                  {
                    this.state['all-bond-funds-'+i8] ?
                      <div styleName='plan-options-table-responsive-more-results-row' >
                        {
                          [
                            // [lbl, val, isFigure, isItalic]
                            ['Asset manager', fam, 0, 0],
                            ['% of fund invested in corporate bonds', fp(cbond_ex), 1, 0],
                            ['% of fund invested in fossil fuel corporate bonds (exposure)', fp(ff_ex), 1, 0],
                            ['% of corporate bond holdings labeled fossil fuel (concentration)', fp(ff_conc), 1, 0],
                          ].map((oLblVal, i9) => {
                            const [lbl, val, isFigure, isItalic] = oLblVal
                            return (
                              <div
                                styleName='responsive-more-results-data-row'
                                key={'all-bond-funds-responsive-more-results-data-row-'+i9}
                              >
                                <div styleName={isItalic ? 'responsive-italic' : ''}>
                                  {lbl}
                                </div>
                                <div styleName={isFigure ? 'responsive-figure' : ''}>
                                  {val}
                                </div>
                              </div>
                            )
                          })
                        }
                      </div> :
                      null
                  }
                </SlideDown>
              </div>
            )
          })
        }
      </div>
    )
  }
}


//
// <TargetDateSelect> component
// Includes: <Select> and onTdSelect()
//             to select an active TD series,
//           <TargetDateResults> to display results
//             for active TD series
//
@CSSModules(Styles, {allowMultiple:true})
class TargetDateSelect extends Component {
  constructor(props) {
    super(props)
    const defaultTdSelectOption = {
      name: '2025 fund for all series',
      label: '2025 fund for all series'
    }
    const tdSelectOptions = [defaultTdSelectOption]
    Object.keys(config.targetDateBondsSource).map((key, i) => {
      tdSelectOptions.push({name: key, label: key})
      return null
    })
    this.state = {
      tdSelected: defaultTdSelectOption,
      tdLoading: false,
      tdTimeout: null,
      tdSelectOptions: tdSelectOptions,
      defaultTdSelectOption
    }
  }

  onTdSelect = (val) => {
    // similar logic as onPlanSelect
    // except there is a default (all 2025 funds)
    // so there is a setTimeout to go back to default
    // even if there is no val selected
    if (this.state.tdTimeout) {
      clearTimeout(this.state.tdTimeout)
    }
    if (val) {
      this.setState({
        tdSelected: val,
        tdLoading: true,
        tdTimeout: setTimeout(()=>{
          this.setState({
            tdLoading: false,
          })
        }, 500)
      })
    } else {
      this.setState({
        tdSelected: this.state.defaultTdSelectOption,
        tdLoading: true,
        tdTimeout: setTimeout(()=>{
          this.setState({
            tdLoading: false,
          })
        }, 500)
      })
    }
  }

  render() {
    const iconTargetDates = require( '../../../static/images/icon-target-dates.png' )
    // select active TD
    let activeTdKey = null
    if (this.state.tdSelected.name !== '2025 fund for all series') {
      activeTdKey = this.state.tdSelected.name
    }
    //
    return (
      <div id='target-dates' styleName='target-date-select-wrap'>
        <ContainerDivs>
          <div styleName='plan-select-wrap'>
            <div styleName='plan-select-flex'>
              <div>
                <h2>Target date fund series</h2>
                <p>
                  Many 401(k)s are based around target date fund series as
                  the default investment option.
                  Select a target date series to see how much it
                  has invested in fossil fuel bonds and stocks across the different
                  dates.
                </p>
                <Select
                  name='td-select'
                  value={this.state.tdSelected}
                  options={this.state.tdSelectOptions}
                  onChange={this.onTdSelect}
                />
              </div>
              <img
                styleName='plan-select-img'
                src={iconTargetDates}
                alt='Target date funds'
              />
            </div>
            <TargetDateResults tdLoading={this.state.tdLoading} activeTdKey={activeTdKey} />
            <div styleName='target-date-results-disclaimer'>
              * To see the figures for these charts,
              download the dataset
              in the <ScrollLink
                activeClass='active-scroll-link'
                to='methodology'
                spy
                smooth
                offset={-70}
                duration={500}
              >
                <span styleName='red-link'>
                  methodology section
                </span>
              </ScrollLink>.
            </div>
          </div>
          <ScrollToTop color='red'/>
        </ContainerDivs>
      </div>
    )
  }
}


//
// <TargetDateResults> component
// Display: chart image for all TD series, 2025 fund,
//          chart images for specific TD series,
//            if there is an active TD series
// @param activeTdKey: Key for the active TD series
// @param tdLoading: T/F to show loading spinner. Controlled by timeout
//                   in <TargetDateSelect> onTdSelect()
//
@CSSModules(Styles, {allowMultiple:true})
class TargetDateResults extends Component {
  static propTypes = {
    activeTdKey: PropTypes.string,
    tdLoading: PropTypes.bool
  }
  render() {
    const urlPrefix = 'https://iyv-charts.s3-us-west-2.amazonaws.com/fossil_fuel_bonds/'
    const allTdsWeightChartUrl = `${urlPrefix}2025 Fund of Target Date Series fossil fuel bonds weight ${currYYYYMM}.png`
    const allTdsWeightChartAlt = '2025 fund of target date series weight chart'
    //
    let tdWeightChartUrl = null
    let tdWeightChartAlt = null
    if (this.props.activeTdKey) {
      tdWeightChartUrl = `${urlPrefix}${this.props.activeTdKey} fossil fuel bonds weight ${currYYYYMM}.png`
      tdWeightChartAlt = `${this.props.activeTdKey} weight chart`
    }
    //
    return (
      <div>
        { /* LOADING ICON DURING PLAN SELECT TIMEOUT */
          this.props.tdLoading ?
            (
              <div styleName='td-loader-wrap'>
                <Loader
                  options={{ scale: 0.70, position: 'relative' }}
                  loaded={false}
                />
              </div>
            ) :
            null
        }

        { /* CHART IMAGE FOR ALL TARGET DATE SERIES */
          !this.props.tdLoading && !this.props.activeTdKey ?
            (
              <div styleName='td-chart-col'>
                <div styleName='plan-summary-col'>
                  <h3 styleName='plan-summary-section-heading'>
                    2025 FUNDS OF POPULAR TARGET DATE SERIES
                  </h3>
                </div>
                <p styleName='chart-disclaimer'>
                  <LinkIcon nospace faclass='fa-info-circle'/>
                  &nbsp;This chart compares 10 popular target date series
                  using the 2025 fund. Those towards the
                  top of the chart have more fossil fuel exposure
                  in the 2025 fund, and those towards the
                  bottom have less exposure. The 2025 fund
                  is used for the comparison
                  as it is more bond-heavy, being marketed to people
                  who are nearing
                  retirement.
                </p>
                <div styleName='chart-img-wrap'>
                  <Link
                    to={allTdsWeightChartUrl}
                    title={'2025 series weight chart'}
                    targetBlank
                  >
                    <img
                      src={allTdsWeightChartUrl}
                      alt={allTdsWeightChartAlt}
                      styleName='chart-img'
                    />
                    <span styleName='chart-expand-label visible-xs'>
                      Click to enlarge<LinkIcon faclass='fa-expand'/>
                    </span>
                  </Link>
                </div>
              </div>
            ) :
            null
        }

        { /* CHART IMAGE TD SERIES - BY WEIGHT */
          !this.props.tdLoading && this.props.activeTdKey ?
            (
              <div styleName='td-chart-col'>
                <div styleName='plan-summary-col'>
                  <h3 styleName='plan-summary-section-heading'>
                    SERIES NAME
                  </h3>
                  <p>
                    {this.props.activeTdKey}
                  </p>
                </div>
                <p styleName='chart-disclaimer'>
                  <LinkIcon nospace faclass='fa-info-circle'/>
                  &nbsp;This chart shows the percent of each
                  fund in the target date series
                  that is invested in fossil fuel bonds
                  and stocks. The funds at the
                  top of the chart are
                  marketed at people further from retirement,
                  and are the most stock-heavy,
                  and the funds at the bottom of the chart
                  are marketed at people closer to retirement,
                  and are more bond-heavy.
                </p>
                <div styleName='chart-img-wrap'>
                  <Link
                    to={tdWeightChartUrl}
                    title={tdWeightChartAlt}
                    targetBlank
                  >
                    <img
                      src={tdWeightChartUrl}
                      alt={tdWeightChartAlt}
                      styleName='chart-img-td'
                    />
                    <span styleName='chart-expand-label visible-xs'>
                      Click to enlarge<LinkIcon faclass='fa-expand'/>
                    </span>
                  </Link>
                </div>
              </div>
            ) :
            null
        }
      </div>
    )
  }
}


//
// <Solutions> component
// Display: text of suggested solutions
// Includes: <BulletAccordion> of suggested solutions
//
@CSSModules(Styles, {allowMultiple:true})
class Solutions extends Component {
  render() {
    return (
      <div styleName='solutions-wrap' id='recommendations'>
        <ContainerDivs>
          <h2>Recommendations</h2>
          <Row>
            <Col xs={12} sm={12} md={6} lg={6}>
              <div styleName='solutions-col-left'>
                <p>
                  The steps retirement plan administrators should take to
                  address the risk of fossil fuel corporate bonds in their
                  plans are similar to the steps they should take to address
                  the risk of fossil fuel stocks. It is the responsibility
                  of every company to ensure that the retirement plan offers
                  sustainable investments.
                </p>
              </div>
            </Col>
            <Col xs={12} sm={12} md={6} lg={6}>
              <div styleName='solutions-col-right'>
                <BulletAccordion bullets={[
                  [
                    'Add climate-safe bond funds',
                    <div>While treasury or other government bond fund
                    options may provide a way for plan participants to
                    invest in bonds without exposure to fossil fuel
                    corporate bonds, they may not be appropriate for all
                    investors. A better solution is to offer diversified
                    bond funds that hold corporate bonds but reduce
                    exposure to carbon intense sectors on the basis of
                    climate risk. <Link
                      xstrong
                      to='https://charts.ussif.org/mfpc'
                      title='Sustainable Investment Mutual Funds and ETFs Chart'
                    >The Forum for Sustainable and Responsible
                    Investment</Link> has a list of sustainably invested
                    funds, including bond funds, many of which seek
                    to mitigate climate risk.</div>
                  ],
                  [
                    'Reduce climate risk of corporate bond holdings in default option',
                    'The default option generally commands a significant amount of the plan assets: new plan participants often have their contributions automatically allocated to the default, and many never switch. This makes it critical that exposure to climate-risky fossil fuel corporate bonds are reduced in these funds, so employees are not automatically having their savings invested in financing the climate crisis.'
                  ],
                  [
                    'Engage asset managers',
                    'Asset managers are responsible for offering fund portfolios to retirement plan administrators that meet the financial needs of employees. If asset managers like Vanguard and BlackRock aren\'t providing appropriate bond fund options that reduce exposure to climate-risky fossil fuel corporate bonds, companies should let them know that their employees deserve climate-safe investments.'
                  ],
                ]}/>
              </div>
            </Col>
          </Row>
          <br/>
          <br/>
          <Contacts />
        </ContainerDivs>
      </div>
    )
  }
}


//
// <Contacts> component
// Display: link to contact form
// Includes: <SignupForm> component
//
@CSSModules(Styles, {allowMultiple:true})
class Contacts extends Component {
  render() {
    return (
      <div>
        <Row>
          <Col xs={12} sm={12} md={6} lg={6}>
            <div styleName='contact-col-left'>
              <h3>
                Questions?
              </h3>
              <p>
                If you have questions about your company's retirement plan,
                get in touch.
              </p>
              <Link to={config.links.contact} title='Contact As You Sow' redbutton>
                <h3 styleName='solutions-contact-link'>
                  Contact us<LinkIcon />
                </h3>
              </Link>
            </div>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <div styleName='contact-col-right '>
              <h3>
                Sign up for updates
              </h3>
              <p>
                We'll let you know when we add new companies, new funds, and
                new features.
              </p>
              <div styleName='solutions-col-signup'>
                <SignupForm url='344561_46053pi_344561_46053' id='344561_46053pi_344561_46053'/>
              </div>
            </div>
        </Col>
        </Row>
        <br/>
        <br/>
        <ScrollToTop color='red'/>
      </div>
    )
  }
}


//
// <Methodology> component
// Display text of methodology
// Display link to spreadsheet of bulk results
//
@CSSModules(Styles, {allowMultiple:true})
class Methodology extends Component {
  constructor(props) {
    super(props)
    this.state = {
      methodologyOpen: false,
      industryListOpen: false
    }
  }

  render() {
    return (
      <div id='methodology' styleName='methodology'>
        <ContainerDivs>
          <h2>Methodology</h2>
          <br/>
          <h3 styleName='methodology-spreadsheet-download'>
            <Link
              to='https://iyv-charts.s3.us-west-2.amazonaws.com/files/Fossil+fuel+bonds+In+401ks+-+As+You+Sow+-+dataset+2023.xlsx'
              title='Fossil fuel bonds In 401ks - As You Sow - dataset 2023.xlsx'
            >
              Download dataset
              <LinkIcon faclass='fa-file-excel-o'/>
              <LinkIcon faclass='fa-download'/>
            </Link>
          </h3>
          <br/>
          <p>
            The results above are based on an analysis of the holdings of mutual funds found in retirement plans of major companies, as well as popular target date funds series used as the default investment option in hundreds of other retirement plans.
          </p>

          <button
            styleName='section-slidedown-button'
            onClick={ () => {
              this.setState({
                methodologyOpen: !this.state.methodologyOpen
              })
            }}
          >
            <h2 styleName='section-slidedown-label'>
              See the full methodology
            </h2>
            <div styleName='section-slidedown-icon'>
              <LinkIcon faclass={ this.state.methodologyOpen ? 'fa-angle-up' : 'fa-angle-down' } />
            </div>
          </button>

          <SlideDown>
            {
              this.state.methodologyOpen ?
                (
                  <div>
                    <h3>
                      AS-OF DATE
                    </h3>
                    <p>
                      This analysis was released in May 2023, using the most recent fund data and retirement plan data available. Fund portfolios are reported monthly or quarterly; retirement plan data is reported annually. For most retirement plans, the data was released in 2022, representing plan options and assets as of 31 December 2021. For most fund portfolios, the data was released at the end of April 2023, or in the few months prior.
                    </p>
                    <h3>
                      RETIREMENT PLANS
                    </h3>
                    <p>
                      The data on retirement plan options is the same used in the equity analysis in the main Retirement Plan Sustainability Scorecard. For more information on how this data is collected from Form 5500 filings on the Department of Labor website, and matched to mutual fund data, see our <Link to={config.routes.methodology} title='Methodology'><span styleName='red-link'>main methodology page</span></Link>.
                    </p>
                    <h3>
                      CORPORATE BONDS
                    </h3>
                    <p>
                      For the purposes of this analysis, as “corporate bonds” we considered different kinds of debt issued by a company in order for it to raise capital. This includes traditional corporate bonds, convertible bonds, corporate inflation-protected bonds, commercial paper, capital-contingent bonds, contingent convertible bonds, and some preferred stock (which are often treated like bonds by investors). Because some fossil fuel companies and projects are state-owned, we also included government agency debt, excluding mortgage issuers like Fannie Mae.
                    </p>
                    <p>
                      Bonds that are not part of this analysis include government bonds (e.g. treasury and muni bonds), mortgage bonds, asset-backed bonds like Collateral Loan Obligations, and more complex securities like derivatives, futures, options, warrants, structured products, and swaps.
                    </p>

                    <h3>
                      TRACKING FOSSIL FUEL BONDS
                    </h3>
                    <p>
                      These corporate bond holdings are then filtered down to fossil fuel companies. The fossil fuel companies searched for include all companies on our <Link to={config.routes.companies+'#fossilfuels'} title='Publicly-traded companies: Fossil fuels'><span styleName='red-link'>publicly-traded fossil fuel screen</span></Link>, as well as non-publicly-traded companies operating in fossil fuel industries. The industry classifications used to identify fossil fuel companies include Standard Industrial Classification (SIC), Industry Classification Benchmark (ICB), and Morningstar industry classifications. The full list of industries classified as fossil fuel can be seen below.
                    </p>
                    <button
                      styleName='methodology-industry-list-btn'
                      onClick={ () => {
                        this.setState({
                          industryListOpen: !this.state.industryListOpen
                        })
                      }}
                    >
                      See fossil fuel industries<LinkIcon faclass={
                        this.state.industryListOpen ? 'fa-angle-down' : 'fa-angle-right'
                      }/>
                    </button>
                    <SlideDown>
                      {
                        this.state.industryListOpen ?
                          (
                            <ul styleName='methodology-industry-list'>
                              {
                                [
                                  'Bituminous coal & lignite mining',
                                  'Bituminous coal & lignite surface mining',
                                  'Bituminous coal underground mining',
                                  'Coal and other minerals and ores',
                                  'Coal mining',
                                  'Coal mining services',
                                  'Coking coal',
                                  'Crude petroleum & natural gas',
                                  'Crude petroleum pipelines',
                                  'Drilling oil & gas wells',
                                  'Electric services',
                                  'Electric, gas & sanitary services',
                                  'Equipment and services for oil and gas extraction',
                                  'Exploration&production',
                                  'Gas & other services combined',
                                  'Liquefied petroleum gas dealers',
                                  'Miscellaneous products of petroleum & coal',
                                  'Natural gas distribution',
                                  'Natural gas liquids',
                                  'Natural gas transmisison & distribution',
                                  'Natural gas transmission',
                                  'Oil & gas',
                                  'Oil & gas drilling',
                                  'Oil & gas E&P',
                                  'Oil & gas equipment & services',
                                  'Oil & gas field exploration services',
                                  'Oil & gas field machinery & equipment',
                                  'Oil & gas field services, NEC',
                                  'Oil & gas integrated',
                                  'Oil & gas midstream',
                                  'Oil & gas refining & marketing',
                                  'Oil and gas extraction',
                                  'Oil and gas extraction and refining',
                                  'Petroleum and coal products',
                                  'Petroleum refining',
                                  'Pipelines (no natural gas)',
                                  'Pipelines',
                                  'Power',
                                  'Refined petroleum pipelines',
                                  'Thermal coal',
                                  'Utilities',
                                  'Utilities-diversified',
                                  'Utilities-independent power producers',
                                  'Utilities-regulated electric',
                                  'Utilities-regulated gas',
                                  'Water, heat and gas supply',
                                  'Wholesale-petroleum & petroleum products (no bulk stations)',
                                  'Wholesale-petroleum bulk stations & terminals'
                                ].map((e, i10) => {
                                  return (
                                    <li key={'fossil-fuel-bond-industry-'+i10}>{e}</li>
                                  )
                                })
                              }
                            </ul>
                          ) :
                          null
                      }
                    </SlideDown>
                    <p>
                      For the utility and electric services industries, any companies in these categories that are engaged in 100% renewable operations, pure transmission, or otherwise don't distribute gas or burn fossil fuels to generate power were not considered “fossil fuel” for the purposes of this analysis. We also excluded a small number of utilities that have fossil fuel operations but meet the criteria for inclusion on our <Link
                        to='https://fossilfreefunds.org/clean-200'
                        title='Clean200'
                      ><span styleName='red-link'>Clean200 screen</span></Link> - USD revenue of at least $1 billion, and over 50% of total revenues are from green sources, as measured by Corporate Knights Research.
                    </p>
                    <p>
                      In addition to industry classifications, several lists from external researchers are used to identify fossil fuel companies. These include the <Link
                        to='https://www.ffisolutions.com/research-analytics-index-solutions/research-screening/the-carbon-underground-200/'
                        title='Carbon Underground 200'
                      ><span styleName='red-link'>Carbon Underground 200</span></Link> from FFI, the <Link
                        to='https://coalexit.org/'
                        title='Global Coal Exit List (GCEL)'
                      ><span styleName='red-link'>Global Coal Exit List (GCEL)</span></Link> and <Link
                        to='https://gogel.org/'
                        title='Global Oil/Gas Exit List (GOGEL)'
                      ><span styleName='red-link'>Global Oil/Gas Exit List (GOGEL)</span></Link> from Urgewald, and the <Link
                        to='https://www.toxicbonds.org/toxic-bonds'
                        title='Toxic Bonds Dirty 30'
                      ><span styleName='red-link'>Toxic Bonds Dirty 30</span></Link>.
                    </p>

                    <p styleName='methodology-callout-p'>
                      <b>What about &ldquo;green bonds&rdquo;?</b> For this analysis,
                      bonds were sorted by issuer,
                      not by project. This means any bonds issued by these companies
                      were considered "fossil fuel corporate" bonds, even if the bonds
                      are marketed as "green bonds".
                      Green bonds are intended to finance projects that contribute
                      positively to the environment or the climate. However,
                      the ‘green finance’ label is highly contested, especially when it
                      comes to companies also involved in the fossil fuel industry.
                    </p>

                    <h3>
                      CALCULATING EXPOSURE AND CONCENTRATION
                    </h3>
                    <p>
                      Once retirement plan options are matched to appropriate mutual funds, and fossil fuel corporate bond holdings identified, exposure is calculated for each mutual fund (defined as the percent of fund assets invested in holdings classified as fossil fuel corporate bonds).
                    </p>
                    <p styleName='methodology-callout-p'>
                      <b>Long holdings only:</b> All exposure and concentration statistics in this analysis are based on long-position holdings only. Short positions and liabilities are ignored.
                    </p>
                    <p>
                      Once each mutual fund has the exposure calculated, the figures are applied to the retirement plan options. Each retirement plan option has some amount of assets ($USD) invested in it by plan participants. Using the fund-level exposures, we calculate summary statistics for each retirement plan:
                    </p>
                    <ul>
                      <li>
                        <b>Investments:</b> The amount of assets ($USD) invested by plan participants in fossil fuel corporate bonds across all plan options
                      </li>
                      <li>
                        <b>Exposure:</b> The percent of all plan assets classified as fossil fuel corporate bonds
                      </li>
                      <li>
                        <b>Concentration:</b> The percent of assets classified as fossil fuel within corporate bond holdings
                      </li>
                    </ul>
                    <p>
                      The same statistics for equity investments in fossil fuels, already collected for the main Retirement Plan Sustainability Scorecard, are included here for comparison.
                    </p>
                    <p>
                      For each retirement plan, we identify the top 10 companies found within fossil fuel corporate bond holdings across all plan options.
                    </p>
                    <p>
                      For each retirement plan, we show two bar charts:
                    </p>
                    <ul>
                      <li>
                        <b>Percent:</b> Showing the exposure percent of each plan option to fossil fuel stocks and corporate bonds
                      </li>
                      <li>
                        <b>$USD:</b> Showing the total assets invested by plan participants in fossil fuel stocks and corporate bonds in each plan option
                      </li>
                    </ul>
                    <h3>
                      TARGET DATE SERIES
                    </h3>
                    <p>
                      We compare the fossil fuel stock and bond exposure of 10 popular target date series using the 2025 fund from each series. The 2025 fund is used for the comparison as it is more bond-heavy, being marketed to people who are nearing retirement. Those fund series towards the top of the chart have more fossil fuel exposure in the 2025 fund, and those towards the bottom have less exposure.
                    </p>
                    <p>
                      For each target date series, we show a chart comparing the fossil fuel stock and bond exposure of the various funds within the series across target dates (2025, 2030, 2035, etc.). Most fund series have more fossil fuel corporate bond exposure as the target dates near the current date.
                    </p>
                    <ScrollToTop color='red'/>
                  </div>
                ) :
                null
            }
          </SlideDown>
        </ContainerDivs>
      </div>
    )
  }
}


//
// <FossilFuelBondsIn401ks> component
// Main page layout
//
@CSSModules(Styles, {allowMultiple:true})
export default class FossilFuelBondsIn401ks extends Component {
  constructor(props) {
    super(props)
    this.state = {
      title: 'Fossil Fuel Bonds in 401(k)s',
      description: 'If there are fossil fuel bonds in your 401(k), ' +
                   'you\'re effectively lending your retirement savings ' +
                   'to companies so they can mine for coal and drill ' +
                   'for oil.',
      url: config.domain + config.routes.fossilFuelBondsIn401ks,
    }
  }

  render() {
    const socialShareUrl =  config.domain+imageSocialShare.replace(/.*dist/,'/dist')
    return (
      <div styleName='page' id='top'>
        { container.helmet(this.state.title,
                           this.state.description,
                           this.state.url) }
        <Helmet meta={[
          {property: 'og:image', content: socialShareUrl},
          {property: 'twitter:image', content: socialShareUrl},
        ]}/>
        <div styleName='article-age-banner'>
          <ContainerDivs >
            <p>This report is {new Date().getFullYear() - 2023} years old, from 2023.</p>
          </ContainerDivs >
        </div>
        <LedeNav title={this.state.title}/>
        <TheIssue />
        <KeyFindings />
        <PlanSection />
        <TargetDateSelect />
        <Solutions />
        <Methodology />
      </div>
    )
  }
}
